import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';

import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import cx from 'classnames';
import { gql, useSuspenseQuery, useMutation, useLazyQuery } from '@apollo/client';
import { Modal, useModalContext } from './common/Modal';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import { TaskModal_GetTaskQuery, TaskModal_GetTaskQueryVariables, TaskModal_GetOrdersQuery, TaskModal_GetOrdersQueryVariables } from '../../codegen/graphql';
import { SearchSelect } from './common/SearchSelect/SearchSelect';
import { TextInput } from './common/TextInput';
import dayjs from 'dayjs';
import Button from './common/Button';
import { useModalRouter } from '../ModalRouter_context';

const TASK_MODAL_ORDER_FRAGMENT = gql`
  fragment TaskModal_Order on Order {
    id
    orderId
    shopId
    shop {
      shopType
    }
    customerName
    orderStatus
    orderDate
    orderProductsVariations {
      quantity
    }
  }
`;

const GET_TASK = gql`
  query TaskModal_GetTask($id: ID!) {
    task(id: $id) {
      id
      title
      description
      orderId
      nextDueDate
      nextActionContent
      completed
      createdAt
      updatedAt
      
      order {
        ...TaskModal_Order
      }
      
      taskEntries {
        id
        content
        date
      }
    }
  }
  ${TASK_MODAL_ORDER_FRAGMENT}
`;

const GET_ORDERS = gql`
  query TaskModal_GetOrders($search: String) {
    orders(filters: { search: $search }) {
      nodes {
        ...TaskModal_Order
      }
    }
  }
  ${TASK_MODAL_ORDER_FRAGMENT}
`;

const CREATE_TASK = gql`
  mutation TaskModal_CreateTask($input: CreateTaskInput!) {
    createTask(input: $input) {
      id
      title
    }
  }
`;

const UPDATE_TASK = gql`
  mutation TaskModal_UpdateTask($id: ID!, $input: UpdateTaskInput!) {
    updateTask(id: $id, input: $input) {
      id
      title
    }
  }
`;

const CREATE_TASK_ENTRY = gql`
  mutation TaskModal_CreateTaskEntry($input: CreateTaskEntryInput!) {
    createTaskEntry(input: $input) {
      id
      content
      date
    }
  }
`;

const DELETE_TASK_ENTRY = gql`
  mutation TaskModal_DeleteTaskEntry($id: ID!) {
    deleteTaskEntry(id: $id)
  }
`;

// const UPDATE_NEXT_ACTION = gql`
//   mutation TaskModal_UpdateNextAction($id: ID!, $input: UpdateNextActionInput!) {
//     updateNextAction(id: $id, input: $input) {
//       id
//       nextDueDate
//       nextActionContent
//     }
//   }
// `;

const NEXT_DUE_DATE_QUICK_OPTIONS = [
  { label: 'Tomorrow', amount: 1, unit: 'day' as const },
  { label: 'In 3 days', amount: 3, unit: 'day' as const },
  { label: 'In 1 week', amount: 1, unit: 'week' as const },
  { label: 'In 10 days', amount: 10, unit: 'day' as const },
  { label: 'In 2 weeks', amount: 2, unit: 'week' as const },
];

function formatNextDueDateFromToday(amount: number, unit: dayjs.ManipulateType): string {
  return dayjs()
    .add(amount, unit)
    .hour(10)
    .minute(0)
    .second(0)
    .millisecond(0)
    .format('YYYY-MM-DDTHH:mm');
}

interface TaskModalProps {
  taskId?: string;
  newTaskOrderId?: string;
  handleClose: () => void;
}

function TaskModal({ taskId, newTaskOrderId }: TaskModalProps) {
  const modalRouter = useModalRouter();

  return (
    <Modal
      title="New Task"
      width="large"
      onClose={modalRouter.pop}
    >
      <TaskModalContent
        taskId={taskId}
        newTaskOrderId={newTaskOrderId}
        handleClose={modalRouter.pop}
      />
    </Modal>
  );
}


function TaskModalContent({ taskId, newTaskOrderId, handleClose }: TaskModalProps) {
  const modalContext = useModalContext();
  const modalRouter = useModalRouter();
  const navigate = useNavigate();
  const isNewTask = taskId === 'new';
  
  const [taskForm, setTaskForm] = useState({
    title: 'New task',
    description: '',
    orderId: newTaskOrderId ? newTaskOrderId : null as string | null,
    nextDueDate: formatNextDueDateFromToday(1, 'day'),
    nextActionContent: '',
    completed: false
  });
  
  const [newEntryContent, setNewEntryContent] = useState('');
  const [newEntryDate, setNewEntryDate] = useState(new Date().toISOString().slice(0, 16));
  
  const [fetchOrders, { data: ordersData }] = useLazyQuery<
    TaskModal_GetOrdersQuery,
    TaskModal_GetOrdersQueryVariables
  >(GET_ORDERS);
  
  const { data: taskData, refetch } = useSuspenseQuery<TaskModal_GetTaskQuery, TaskModal_GetTaskQueryVariables>(
    GET_TASK,
    {
      variables: { id: taskId || '' },
      fetchPolicy: 'cache-and-network',
      skip: isNewTask,
    }
  );
  
  const [createTask] = useMutation(CREATE_TASK, {
    onCompleted: (data) => {
      modalRouter.push({ s: 'task', a: { taskId: data.createTask.id } });
    },
  });
  
  const [updateTask] = useMutation(UPDATE_TASK, {
    onCompleted: () => {
      refetch();
    },
  });
  
  const [createTaskEntry] = useMutation(CREATE_TASK_ENTRY, {
    onCompleted: () => {
      setNewEntryContent('');
      refetch();
    },
  });
  
  const [deleteTaskEntry] = useMutation(DELETE_TASK_ENTRY, {
    onCompleted: () => {
      refetch();
    },
  });
  
  // const [updateNextAction] = useMutation(UPDATE_NEXT_ACTION, {
  //   onCompleted: () => {
  //     refetch();
  //   },
  // });
  
  useLayoutEffect(() => {
    if (taskData?.task && !isNewTask) {
      setTaskForm({
        title: taskData.task.title,
        description: taskData.task.description || '',
        orderId: taskData.task.orderId || null,
        nextDueDate: dayjs(taskData.task.nextDueDate).format('YYYY-MM-DDTHH:mm'),
        nextActionContent: taskData.task.nextActionContent || '',
        completed: taskData.task.completed
      });
    }
  }, [taskData, isNewTask]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setTaskForm(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'datetime-local') {
      const date = dayjs(value).format('YYYY-MM-DDTHH:mm');
      setTaskForm(prev => ({ ...prev, [name]: value === '' ? null : date }));
    } else {
      setTaskForm(prev => ({ ...prev, [name]: value === '' ? null : value }));
    }
  };
  
  const handleSaveTask = useCallback(() => {
    const input = {
      ...taskForm,
      orderId: taskForm.orderId ? parseInt(taskForm.orderId) : null,
      nextDueDate: taskForm.nextDueDate ? new Date(taskForm.nextDueDate).toISOString() : null
    };

    if (isNewTask) {
      createTask({ variables: { input } });
    } else {
      updateTask({ variables: { id: taskId, input } });
    }
  }, [isNewTask, taskForm, createTask, updateTask, taskId]);
  
  const handleAddEntry = () => {
    if (!newEntryContent.trim() || isNewTask) return;
    
    createTaskEntry({
      variables: {
        input: {
          taskId,
          content: newEntryContent,
          date: new Date(newEntryDate).toISOString()
        }
      }
    });
  };
  
  const handleDeleteEntry = (entryId: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      deleteTaskEntry({ variables: { id: entryId } });
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadOrderOptions = async (searchText: string) => {
    if (!searchText) return [];

    const { data } = await fetchOrders({ variables: { search: searchText } });
    return data?.orders.nodes || [];
  };

  const renderOrderOption = (
    order: TaskModal_GetOrdersQuery['orders']['nodes'][0],
    isRenderingSelected: boolean,
    selectCallback?: () => void
  ) => {
    const totalItems = order.orderProductsVariations.reduce((sum, item) => sum + item.quantity, 0);
    return (
      <div
        className={cx([
          'flex-1',
          !isRenderingSelected && 'px-4 py-2 hover:bg-gray-100 cursor-pointer'
        ])}
        onClick={selectCallback || undefined}
      >
        <div className="flex justify-between items-center">
          <div className="font-medium">{order.shop.shopType}</div>
          <div className="text-gray-500">#{order.orderId}</div>
        </div>
        <div className="flex justify-between items-center">
          <div className="text-md text-gray-600">
            {order.customerName}
          </div>
          <div className="text-md text-gray-600 mt-1">
            {new Date(order.orderDate).toLocaleDateString('fr-FR')}
          </div>
        </div>
        <div className="text-md text-gray-600">
          {totalItems} items • TODO €
        </div>
      </div>
    );
  };

  useLayoutEffect(() => {
    modalContext.setTitle(
      <div>
        <TextInput
          name="title"
          value={taskForm.title}
          onChange={handleInputChange}
          required
        />
      </div>
    );
    modalContext.setActions([
      {
        children: 'Cancel',
        preset: 'white',
        onClick: handleClose,
      },
      {
        children: isNewTask ? 'Create Task' : 'Save Changes',
        preset: 'primary',
        onClick: handleSaveTask,
        disabled: !taskForm.title,
      },
    ]);
  }, [isNewTask, taskForm.title, handleInputChange, handleClose, handleSaveTask]);

  return (
    <div className="grid grid-cols-2 gap-6">
      <div className="space-y-6">
        <div>
          <label htmlFor="description" className="block text-md font-medium text-gray-700">Description</label>
          <textarea
            id="description"
            name="description"
            value={taskForm.description || ''}
            onChange={handleInputChange}
            rows={4}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-xs py-2 px-3 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 md:text-md"
          />
        </div>
        
        <div>
          <label htmlFor="orderId" className="block text-md font-medium text-gray-700">Related Order</label>
          <SearchSelect
            value={
              taskForm.orderId
                ? ordersData?.orders.nodes.find(o => o.id === taskForm.orderId) || taskData?.task?.order || null
                : null
            }
            loadOptions={loadOrderOptions}
            renderOption={(order, selectCallback) => renderOrderOption(order, false, selectCallback)}
            renderSelected={(order) => renderOrderOption(order, true)}
            onSelect={(order) => {
              console.log('order', order);
              setTaskForm(prev => ({ ...prev, orderId: order.id }));
            }}
            onClear={() => setTaskForm(prev => ({ ...prev, orderId: null }))}
            placeholder="Search orders..."
            className="mt-1"
            dropdownWidth={400}
            dropdownHeight={300}
          />

          {taskForm.orderId && (
            <Button
              Component={Link}
              to={`/orders/${taskForm.orderId}`}
              preset="white"
            >
              View Order
            </Button>
          )}
        </div>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="completed"
            name="completed"
            checked={taskForm.completed}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-sm"
          />
          <label htmlFor="completed" className="ml-2 block text-md text-gray-900">
            Mark as completed
          </label>
        </div>
      </div>
      
      <div className="border-t md:border-t-0 md:border-l border-gray-200 pt-6 md:pt-0 md:pl-6">
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Next Action</h3>
          <div>
            <label htmlFor="nextActionContent" className="block text-md font-medium text-gray-700">Next Action Content</label>
            <textarea
              id="nextActionContent"
              name="nextActionContent"
              value={taskForm.nextActionContent}
              onChange={handleInputChange}
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-xs py-2 px-3 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 md:text-md"
            />
          </div>
          <div>
            <label htmlFor="nextDueDate" className="block text-md font-medium text-gray-700">Next Due Date</label>
            <input
              type="datetime-local"
              id="nextDueDate"
              name="nextDueDate"
              value={taskForm.nextDueDate || ''}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-xs py-2 px-3 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 md:text-md"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {NEXT_DUE_DATE_QUICK_OPTIONS.map(({ label, amount, unit }) => (
                <Button
                  key={label}
                  preset="white"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setTaskForm(prev => ({
                      ...prev,
                      nextDueDate: formatNextDueDateFromToday(amount, unit),
                    }));
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        {!isNewTask && (
          <div className="mb-4">
            <div className="flex flex-col space-y-2">
              <div className="flex">
                <input
                  type="text"
                  value={newEntryContent}
                  onChange={(e) => setNewEntryContent(e.target.value)}
                  placeholder="Add a new entry..."
                  className="grow border border-gray-300 rounded-l-md shadow-xs py-2 px-3 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 md:text-md"
                />
                <button
                  onClick={handleAddEntry}
                  disabled={!newEntryContent.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                >
                  <MdAdd size={20} />
                </button>
              </div>
              <input
                type="datetime-local"
                value={newEntryDate}
                onChange={(e) => setNewEntryDate(e.target.value)}
                className="border border-gray-300 rounded-md shadow-xs py-2 px-3 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 md:text-md"
              />
            </div>
          </div>
        )}
        
        {isNewTask ? (
          <div className="text-md text-gray-500 italic">
            Task entries can be added after creating the task.
          </div>
        ) : taskData!.task?.taskEntries.length === 0 ? (
          <div className="text-md text-gray-500 italic">
            No entries yet. Add one using the form above.
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
            {taskData!.task!.taskEntries.map(entry => (
              <div key={entry.id} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-start">
                  <div className="text-md font-medium text-gray-900">
                    {formatDate(entry.date)}
                  </div>
                  <button
                    onClick={() => handleDeleteEntry(entry.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <MdDeleteOutline size={18} />
                  </button>
                </div>
                <div className="mt-1 text-md text-gray-700 whitespace-pre-wrap">
                  {entry.content}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Outlet />
    </div>
  );
}

export default TaskModal; 